import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Copy, Check, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from '@clareefai/ui';
import {
  useGetWidgetConfigQuery,
  useUpdateWidgetConfigMutation,
} from '../../store/vendorApi';
import WidgetPreview from '../../components/settings/WidgetPreview';

const VARIANTS = ['floating', 'bubble', 'card', 'inline', 'banner'] as const;
const POSITIONS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const;
// Corner placement only applies to the variants that float over the page.
const POSITIONED_VARIANTS = ['floating', 'bubble', 'card'];

const WidgetSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: config, isLoading } = useGetWidgetConfigQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdateWidgetConfigMutation();
  const [copied, setCopied] = useState(false);

  // Local form state mirrors the saved config; we send only the fields the vendor edits.
  const [buttonText, setButtonText] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [variant, setVariant] = useState<string | null>(null);
  const [position, setPosition] = useState<string | null>(null);

  const effectiveButtonText = buttonText ?? config?.buttonText ?? 'Leave us a review';
  const effectiveColor = color ?? config?.color ?? '#4F46E5';
  const effectiveVariant = variant ?? config?.variant ?? 'floating';
  const effectivePosition = position ?? config?.position ?? 'bottom-right';

  if (isLoading || !config) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const save = async (overrides: Record<string, unknown>) => {
    try {
      await updateConfig({
        buttonText: effectiveButtonText,
        color: effectiveColor,
        variant: effectiveVariant,
        position: effectivePosition,
        ...overrides,
      }).unwrap();
      toast.success(t('widget.saved', 'Widget settings saved'));
    } catch {
      toast.error(t('widget.saveError', 'Could not save. Please try again.'));
    }
  };

  const copySnippet = async () => {
    if (!config.snippet) return;
    await navigator.clipboard.writeText(config.snippet);
    setCopied(true);
    toast.success(t('widget.copied', 'Snippet copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('widget.title', 'Backoffice Widget')}</h1>
          <p className="text-sm text-gray-600">
            {t(
              'widget.subtitle',
              'Embed a button in your own app so your customers can sign up as promoters.',
            )}
          </p>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">{t('widget.enable', 'Enable widget')}</p>
          <p className="text-sm text-gray-600">
            {config.isEnabled
              ? t('widget.enabledHint', 'Your widget is live and accepting signups.')
              : t('widget.disabledHint', 'Turn on to generate your embed key and snippet.')}
          </p>
        </div>
        <button
          onClick={() => save({ isEnabled: !config.isEnabled })}
          disabled={isSaving}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
            config.isEnabled ? 'bg-purple-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {config.isEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="space-y-6">
          {/* Styling */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">{t('widget.appearance', 'Appearance')}</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('widget.buttonText', 'Button text')}
              </label>
              <input
                type="text"
                value={effectiveButtonText}
                onChange={(e) => setButtonText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('widget.color', 'Accent color')}
                </label>
                <input
                  type="color"
                  value={effectiveColor}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-16 rounded border border-gray-300"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('widget.variant', 'Style')}
                </label>
                <select
                  value={effectiveVariant}
                  onChange={(e) => setVariant(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {VARIANTS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              {POSITIONED_VARIANTS.includes(effectiveVariant) && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('widget.position', 'Corner')}
                  </label>
                  <select
                    value={effectivePosition}
                    onChange={(e) => setPosition(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {POSITIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {t(
                'widget.placementHint',
                'Card & inline drop into a #clareefai-widget container on your page; floating, bubble & banner overlay it.',
              )}
            </p>

            <button
              onClick={() => save({})}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('common.save', 'Save')}
            </button>
          </div>

          {/* Snippet */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{t('widget.snippet', 'Embed snippet')}</h2>
              <button
                onClick={() => save({ regenerateKey: true })}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-60"
                title={t('widget.regenerate', 'Regenerate key (invalidates existing embeds)') as string}
              >
                <RefreshCw className="w-4 h-4" />
                {t('widget.regenerate', 'Regenerate key')}
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {t('widget.snippetHint', 'Paste this into your app, just before the closing </body> tag.')}
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all">
                {config.snippet}
              </pre>
              <button
                onClick={copySnippet}
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? t('widget.copiedShort', 'Copied') : t('widget.copy', 'Copy')}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-600">{t('widget.impressions', 'Impressions')}</p>
              <p className="text-2xl font-bold text-gray-900">{config.impressions.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-600">{t('widget.signups', 'Signups')}</p>
              <p className="text-2xl font-bold text-gray-900">{config.signups.toLocaleString()}</p>
            </div>
          </div>
          </div>

          {/* Live preview */}
          <div className="lg:sticky lg:top-6">
            <WidgetPreview
              variant={effectiveVariant}
              color={effectiveColor}
              buttonText={effectiveButtonText}
              position={effectivePosition}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetSettings;
